// AIDAN — Automated Index for Data Asset Nomenclature
// Reverse-engineered requirements catalogue, derived from the implemented
// codebase (worker routes, React pages, schema, e2e tests). Consumed by the
// React About page to render a live, source-cited spec.

export interface Requirement {
  id: string;
  category: "functional" | "non-functional";
  area: string;
  title: string;
  description: string;
  acceptance: string[];
  source: string[];
  priority: "P0" | "P1" | "P2";
  status: "implemented" | "partial" | "planned";
}

export interface RequirementGroup {
  area: string;
  description: string;
  requirements: string[];
}

export const requirements: Requirement[] = [
  // -------------------------------------------------------------------------
  // Records
  // -------------------------------------------------------------------------
  {
    id: "FR-RECORDS-CRUD",
    category: "functional",
    area: "Records",
    title: "Create, read, update and soft-delete records",
    description:
      "Every reference-data record in any domain supports a full CRUD lifecycle. Updates bump the version counter and write a `record_versions` snapshot. Deletes are soft (status flips to `deprecated`) so history is preserved.",
    acceptance: [
      "POST /api/domains/:id/records creates a v1 record and an audit event",
      "PATCH /api/domains/:id/records/:rid increments version and stores a diff",
      "DELETE /api/domains/:id/records/:rid sets status='deprecated' (row retained)",
      "GET /api/domains/:id/records/:rid returns 404 for unknown ids",
    ],
    source: [
      "worker/routes/records.ts:104",
      "worker/routes/records.ts:112",
      "worker/routes/records.ts:134",
      "worker/routes/records.ts:164",
      "worker/db/schema.sql:37",
    ],
    priority: "P0",
    status: "implemented",
  },
  {
    id: "FR-RECORDS-LIST",
    category: "functional",
    area: "Records",
    title: "List records with pagination, sort and filter",
    description:
      "The records list endpoint supports query string `q`, `status`, `sort=col:dir`, `page` and `pageSize` (capped at 500). Sortable columns are whitelisted to prevent SQL injection.",
    acceptance: [
      "GET /api/domains/:id/records?page=0&pageSize=50 returns { items, total, page, pageSize }",
      "sort param accepts id|status|version|updated_at|created_at|quality_score with :asc|:desc",
      "Invalid sort columns fall back to updated_at DESC",
      "pageSize is clamped to [1, 500]",
    ],
    source: ["worker/routes/records.ts:67", "src/pages/Browse.tsx:1"],
    priority: "P0",
    status: "implemented",
  },
  {
    id: "FR-RECORDS-VERSIONING",
    category: "functional",
    area: "Records",
    title: "Append-only version history per record",
    description:
      "Every create/update writes an immutable row to `record_versions` with the full data snapshot, computed diff, author and comment.",
    acceptance: [
      "GET /api/domains/:id/records/:rid/versions returns DESC by version",
      "Each PATCH appends a new version row with diff[]",
      "Restoring a version creates a new version (does not rewrite history)",
    ],
    source: [
      "worker/routes/versions.ts:22",
      "worker/routes/versions.ts:56",
      "worker/db/schema.sql:76",
    ],
    priority: "P0",
    status: "implemented",
  },
  {
    id: "FR-RECORDS-EXPORT",
    category: "functional",
    area: "Records",
    title: "Export records as CSV or JSON",
    description:
      "Domain export streams up to 5,000 records as CSV (with CRLF-safe quoting) or JSON. XLSX is intentionally out of scope — TSV via Excel covers the use case.",
    acceptance: [
      "GET /api/domains/:id/records/export?format=csv returns text/csv",
      "GET /api/domains/:id/records/export?format=json returns application/json",
      "Header row is `id,status,version,<union-of-data-keys>`",
      "Values containing comma/quote/newline are quoted and double-escaped",
    ],
    source: ["worker/routes/records.ts:46", "src/pages/Browse.tsx:1"],
    priority: "P1",
    status: "implemented",
  },
  {
    id: "FR-RECORDS-DETAIL",
    category: "functional",
    area: "Records",
    title: "Record detail view with tabbed inspector",
    description:
      "RecordDetail shows overview, versions, lineage (mini), quality and audit tabs for a single record, with breadcrumb navigation.",
    acceptance: [
      "Route /browse/:domain/:recordId renders RecordDetail",
      "Tabs switch without leaving the route",
      "Breadcrumb links back to the domain table",
    ],
    source: ["src/pages/RecordDetail.tsx:1", "src/router.tsx:29"],
    priority: "P1",
    status: "implemented",
  },

  // -------------------------------------------------------------------------
  // Domains
  // -------------------------------------------------------------------------
  {
    id: "FR-DOMAINS-LIST",
    category: "functional",
    area: "Domains",
    title: "List domains with live record counts and owners",
    description:
      "GET /api/domains returns each domain joined with its current record count and most-recent record timestamp, used by the side rail badges and dashboard.",
    acceptance: [
      "Response items contain id, name, owner, icon, recordCount, lastUpdated",
      "recordCount is computed by COUNT(*) on records joined per domain",
      "Single domain endpoint returns the field schema list",
    ],
    source: [
      "worker/routes/domains.ts:8",
      "worker/routes/domains.ts:34",
      "worker/routes/domains.ts:65",
    ],
    priority: "P0",
    status: "implemented",
  },

  // -------------------------------------------------------------------------
  // Versions
  // -------------------------------------------------------------------------
  {
    id: "FR-VERSIONS-DIFF",
    category: "functional",
    area: "Versions",
    title: "Compute and render diff between any two versions",
    description:
      "Server-side diff endpoint returns the field-level delta between versions A and B. The Versions page renders side-by-side `before/after` strikethrough/highlight cells.",
    acceptance: [
      "GET /api/domains/:id/records/:rid/versions/diff?a=1&b=3 returns { diff:[{key,before,after}] }",
      "Diff includes keys that appear in either snapshot",
      "Unchanged keys are omitted",
    ],
    source: ["worker/routes/versions.ts:31", "src/pages/Versions.tsx:1"],
    priority: "P1",
    status: "implemented",
  },
  {
    id: "FR-VERSIONS-RESTORE",
    category: "functional",
    area: "Versions",
    title: "Restore a prior version into the live record",
    description:
      "Restore copies a chosen version's payload into the live row, increments the version counter, writes a new version snapshot and an `records.restore` audit event. The spec calls for restore to flow through the approval queue; today it commits directly.",
    acceptance: [
      "POST /api/domains/:id/records/:rid/versions/:v/restore returns the new live record",
      "A new record_versions row is appended (history retained)",
      "An audit_events row with action='records.restore' is written",
      "Future: restore creates a pending approval instead of mutating directly",
    ],
    source: ["worker/routes/versions.ts:56", "SPEC.md:131"],
    priority: "P1",
    status: "partial",
  },

  // -------------------------------------------------------------------------
  // Approvals
  // -------------------------------------------------------------------------
  {
    id: "FR-APPROVALS-FLOW",
    category: "functional",
    area: "Approvals",
    title: "Approve, reject or withdraw a pending approval",
    description:
      "Approvals capture the maker, change summary, diff, impact count and priority. State transitions are recorded against the checker with an optional comment.",
    acceptance: [
      "POST /approvals/:id/approve sets status='approved', checker, checked_at",
      "POST /approvals/:id/reject sets status='rejected'",
      "POST /approvals/:id/withdraw sets status='withdrawn'",
      "Optional `comment` is appended to approval_comments",
      "Every transition writes audit_events `approvals.<status>`",
    ],
    source: ["worker/routes/approvals.ts:64", "worker/routes/approvals.ts:85"],
    priority: "P0",
    status: "implemented",
  },
  {
    id: "FR-APPROVALS-BULK",
    category: "functional",
    area: "Approvals",
    title: "Bulk-approve selected pending approvals",
    description:
      "The Approvals page exposes a sticky bulk-action bar that calls /approvals/bulk-approve with the selected ids; the server runs each transition independently and returns { ok, fail } counts.",
    acceptance: [
      "POST /approvals/bulk-approve accepts { ids: string[] }",
      "Only items still in `pending` are transitioned",
      "Response is { ok, fail } counts",
    ],
    source: ["worker/routes/approvals.ts:89", "src/pages/Approvals.tsx:1"],
    priority: "P1",
    status: "implemented",
  },
  {
    id: "FR-APPROVALS-DUAL-CONTROL",
    category: "functional",
    area: "Approvals",
    title: "Maker-checker dual control (no self-approval)",
    description:
      "The spec mandates a maker cannot approve their own change. The current transition handler updates approvals regardless of who the maker was — the dual-control guard is not enforced server-side.",
    acceptance: [
      "Given maker===checker, approve returns 403",
      "Audit log retains the rejected attempt",
      "Bulk-approve skips items where maker===caller",
    ],
    source: ["worker/routes/approvals.ts:64", "SPEC.md:136"],
    priority: "P0",
    status: "planned",
  },
  {
    id: "FR-APPROVALS-COMMENTS",
    category: "functional",
    area: "Approvals",
    title: "Threaded comments on each approval",
    description:
      "Each approval has a chronological comment thread written by makers and checkers; comments are loaded with the approval.",
    acceptance: [
      "POST /approvals/:id/comments accepts { body }",
      "GET /approvals/:id includes comments[] ordered ASC by `at`",
    ],
    source: ["worker/routes/approvals.ts:104", "worker/routes/approvals.ts:10"],
    priority: "P2",
    status: "implemented",
  },
  {
    id: "FR-APPROVALS-SLA",
    category: "functional",
    area: "Approvals",
    title: "SLA / age signalling for pending approvals",
    description:
      "The dashboard surfaces a `pendingDelta` (today vs yesterday) and prioritises pending items by `priority` then `maker_at`. A formal SLA breach indicator (e.g. red badge after N hours) is not yet wired.",
    acceptance: [
      "Pending count and stale-from-yesterday delta appear on the dashboard",
      "Pending list orders by priority then maker_at",
      "Items older than the configured SLA visibly flag a breach",
    ],
    source: ["worker/routes/stats.ts:20", "worker/routes/approvals.ts:32"],
    priority: "P1",
    status: "partial",
  },

  // -------------------------------------------------------------------------
  // Hierarchies
  // -------------------------------------------------------------------------
  {
    id: "FR-HIERARCHIES-TREE",
    category: "functional",
    area: "Hierarchies",
    title: "Render hierarchical taxonomies as a tree",
    description:
      "GET /hierarchies returns each hierarchy with its nodes assembled into a parent→children tree. The page renders a disclosure tree with badges and drag-reparent affordance.",
    acceptance: [
      "Response includes rootNodes[] with recursive children",
      "Orphan nodes (parent_id not in set) become root nodes",
      "Tree respects hierarchy_nodes.sort_order",
    ],
    source: ["worker/routes/hierarchies.ts:23", "src/pages/Hierarchies.tsx:1"],
    priority: "P1",
    status: "implemented",
  },
  {
    id: "FR-HIERARCHIES-MUTATIONS",
    category: "functional",
    area: "Hierarchies",
    title: "Add, rename, reparent and delete nodes with cascade",
    description:
      "Nodes can be added under any parent, renamed/re-badged, dragged to a new parent (`reparent`), or deleted — deletion cascades to all descendants via in-memory traversal.",
    acceptance: [
      "POST /hierarchies/:id/nodes creates a node with optional parentId",
      "PATCH /hierarchies/:id/nodes/:nodeId edits label and badge only",
      "POST /hierarchies/:id/reparent writes an audit event",
      "DELETE removes the node and every transitive descendant",
    ],
    source: [
      "worker/routes/hierarchies.ts:52",
      "worker/routes/hierarchies.ts:63",
      "worker/routes/hierarchies.ts:73",
      "worker/routes/hierarchies.ts:87",
    ],
    priority: "P1",
    status: "implemented",
  },

  // -------------------------------------------------------------------------
  // Lineage
  // -------------------------------------------------------------------------
  {
    id: "FR-LINEAGE-GRAPH",
    category: "functional",
    area: "Lineage",
    title: "Render upstream / downstream lineage neighbourhood",
    description:
      "Given a record, the lineage endpoint BFS-traverses lineage_edges to a configurable depth and returns nodes + edges for SVG rendering with curved Bezier paths.",
    acceptance: [
      "GET /lineage/:domain/:recordId?depth=N returns { root, nodes[], edges[] }",
      "Depth is clamped to [1, 5]",
      "Records absent from lineage_nodes get a synthetic single-node response",
      "Edge confidence colour-codes the stroke (high=forest, med=ochre, low=rust)",
    ],
    source: ["worker/routes/lineage.ts:36", "src/pages/Lineage.tsx:1"],
    priority: "P1",
    status: "implemented",
  },
  {
    id: "FR-LINEAGE-IMPACT",
    category: "functional",
    area: "Lineage",
    title: "Downstream impact preview ('where-used')",
    description:
      "The impact endpoint counts and previews downstream consumers up to depth 3, used by the Approvals page and the Lineage 'impact mode' toggle.",
    acceptance: [
      "GET /lineage/:domain/:recordId/impact returns { affected, preview[] }",
      "Preview is capped at 20 nodes",
      "Root node is excluded from the count",
    ],
    source: ["worker/routes/lineage.ts:48"],
    priority: "P1",
    status: "implemented",
  },
  {
    id: "FR-LINEAGE-CONTROLS",
    category: "functional",
    area: "Lineage",
    title: "Depth slider, kind filter and layout toggle",
    description:
      "The Lineage page UI exposes a depth slider (1–4), kind filter chips, and an Impact mode toggle that highlights downstream-only paths.",
    acceptance: [
      "Depth slider changes refetch the graph",
      "Kind chips filter edges client-side without refetch",
      "Impact toggle dims non-downstream edges",
    ],
    source: ["src/pages/Lineage.tsx:1"],
    priority: "P2",
    status: "implemented",
  },

  // -------------------------------------------------------------------------
  // Mappings
  // -------------------------------------------------------------------------
  {
    id: "FR-MAPPINGS-SETS",
    category: "functional",
    area: "Mappings",
    title: "List mapping sets between source and target domains",
    description:
      "Mapping sets pair a source and target domain; each set contains many mapping items with a confidence score and verification flag.",
    acceptance: [
      "GET /mappings returns sets with itemCount and lastSyncedAt",
      "POST /mappings creates a new set",
      "GET /mappings/:setId/items supports q, minConfidence, page, pageSize",
    ],
    source: ["worker/routes/mappings.ts:8", "worker/routes/mappings.ts:24"],
    priority: "P1",
    status: "implemented",
  },
  {
    id: "FR-MAPPINGS-VERIFY",
    category: "functional",
    area: "Mappings",
    title: "Verify, override or auto-map items",
    description:
      "Mapping items can be manually verified, individually overridden (target/confidence/notes), or auto-mapped via a heuristic endpoint that copies source_id to target_id with 0.85 confidence. A true similarity model (string distance / embeddings) is not yet wired.",
    acceptance: [
      "POST /mappings/:setId/items/:itemId/verify flips verified=1 and writes audit",
      "PATCH /mappings/:setId/items/:itemId updates target_id/confidence/notes and marks verified",
      "POST /mappings/:setId/auto-map returns the count of newly-mapped items",
      "Future: auto-map uses string distance / vector similarity rather than identity",
    ],
    source: [
      "worker/routes/mappings.ts:47",
      "worker/routes/mappings.ts:61",
      "worker/routes/mappings.ts:81",
    ],
    priority: "P1",
    status: "partial",
  },

  // -------------------------------------------------------------------------
  // Quality
  // -------------------------------------------------------------------------
  {
    id: "FR-QUALITY-RULES",
    category: "functional",
    area: "Quality",
    title: "Manage data-quality rules per domain",
    description:
      "Quality rules carry an expression, severity (warn/error) and enabled flag. Rules can be created, edited and toggled.",
    acceptance: [
      "GET /quality/rules?domainId= filters to a single domain",
      "POST /quality/rules creates a rule with severity default 'warn'",
      "PATCH /quality/rules/:id can toggle enabled",
    ],
    source: [
      "worker/routes/quality.ts:8",
      "worker/routes/quality.ts:22",
      "worker/routes/quality.ts:32",
    ],
    priority: "P1",
    status: "implemented",
  },
  {
    id: "FR-QUALITY-RUNS",
    category: "functional",
    area: "Quality",
    title: "Run rules and aggregate pass/fail counts",
    description:
      "Single-rule and run-all endpoints return aggregated pass/fail counts. The current implementation aggregates pre-existing quality_results rows rather than re-evaluating expressions live against records.",
    acceptance: [
      "POST /quality/rules/:id/run returns { pass, fail }",
      "POST /quality/rules/run-all returns { rules, pass, fail }",
      "Re-running a rule re-evaluates its expression against current records and writes new results",
    ],
    source: ["worker/routes/quality.ts:91", "worker/routes/quality.ts:99"],
    priority: "P1",
    status: "partial",
  },
  {
    id: "FR-QUALITY-SCORECARD",
    category: "functional",
    area: "Quality",
    title: "Per-domain scorecard with trend and violations",
    description:
      "Each domain exposes a scorecard with passing/failing counts, by-rule breakdown and a 14-point trend sparkline; violations are paged separately. The trend currently uses a synthetic series (no historical store yet).",
    acceptance: [
      "GET /quality/score/:domainId returns totals, byRule[] and trend[14]",
      "GET /quality/violations?domainId returns failing results joined with rule meta",
      "Trend is persisted across days rather than synthesised",
    ],
    source: ["worker/routes/quality.ts:47", "worker/routes/quality.ts:68"],
    priority: "P1",
    status: "partial",
  },

  // -------------------------------------------------------------------------
  // Imports
  // -------------------------------------------------------------------------
  {
    id: "FR-IMPORTS-UPLOAD",
    category: "functional",
    area: "Imports",
    title: "Upload a file and create a mapping job",
    description:
      "Multipart upload stores the file in R2 and creates an import_jobs row in `mapping` state, ready for the column-mapping wizard.",
    acceptance: [
      "POST /api/imports accepts multipart with file + domainId",
      "File body is streamed to FILES R2 bucket at imports/<id>/<filename>",
      "Job row is created with status='mapping'",
      "audit_events `imports.upload` is appended",
    ],
    source: ["worker/routes/imports.ts:29"],
    priority: "P1",
    status: "implemented",
  },
  {
    id: "FR-IMPORTS-COMMIT",
    category: "functional",
    area: "Imports",
    title: "Confirm column mapping and ingest rows",
    description:
      "After mapping, the user confirms and the server should parse the R2 file, insert records and report per-row results. The current implementation marks the job complete with synthetic counts (rowsTotal=100, ok=96, error=4) — no rows are actually ingested.",
    acceptance: [
      "POST /imports/:id/confirm accepts { mapping }",
      "Job transitions to status='complete' with real rowsTotal/rowsOk/rowsError",
      "Successful rows are inserted into `records` honouring maker-checker",
      "An error-report file is uploaded to R2 (errorReportKey) for failed rows",
    ],
    source: ["worker/routes/imports.ts:49"],
    priority: "P0",
    status: "planned",
  },
  {
    id: "FR-IMPORTS-HISTORY",
    category: "functional",
    area: "Imports",
    title: "List and cancel import jobs",
    description:
      "GET /imports lists the 100 most-recent jobs; POST /:id/cancel transitions a job to `failed` so retries can be triggered.",
    acceptance: [
      "GET /api/imports returns latest 100 jobs DESC by created_at",
      "GET /api/imports/:id returns a single job",
      "POST /api/imports/:id/cancel sets status='failed' and finished_at=now",
    ],
    source: [
      "worker/routes/imports.ts:17",
      "worker/routes/imports.ts:22",
      "worker/routes/imports.ts:66",
    ],
    priority: "P2",
    status: "implemented",
  },

  // -------------------------------------------------------------------------
  // Integrations
  // -------------------------------------------------------------------------
  {
    id: "FR-INTEGRATIONS-API-KEYS",
    category: "functional",
    area: "Integrations",
    title: "Create, list and revoke API keys",
    description:
      "API keys are minted with a random 32-char suffix; only the SHA-256 hash and an 8-char prefix are persisted. The raw key is returned once on creation and never again.",
    acceptance: [
      "POST /integrations/api-keys returns { key, meta } once",
      "Database stores only hash + prefix (no raw secret)",
      "DELETE /integrations/api-keys/:id sets revoked=1 (soft revoke)",
      "List omits the hash field entirely",
    ],
    source: [
      "worker/routes/integrations.ts:23",
      "worker/routes/integrations.ts:33",
      "worker/routes/integrations.ts:51",
    ],
    priority: "P0",
    status: "implemented",
  },
  {
    id: "FR-INTEGRATIONS-WEBHOOKS",
    category: "functional",
    area: "Integrations",
    title: "Register, test and delete webhooks",
    description:
      "Webhooks subscribe to event names; the secret is hashed like API keys. A test endpoint POSTs a `{ test: true }` payload to verify connectivity and records last_status.",
    acceptance: [
      "POST /integrations/webhooks creates a webhook with hashed secret",
      "POST /integrations/webhooks/:id/test fires the URL and records ms + status",
      "Test response is { ok, status, ms }",
      "DELETE removes the row permanently",
    ],
    source: [
      "worker/routes/integrations.ts:57",
      "worker/routes/integrations.ts:65",
      "worker/routes/integrations.ts:82",
    ],
    priority: "P1",
    status: "implemented",
  },
  {
    id: "FR-INTEGRATIONS-SYNC-LOGS",
    category: "functional",
    area: "Integrations",
    title: "Recent sync activity log",
    description:
      "The sync-logs endpoint surfaces relevant audit_events (imports.*, integrations.*, records.*) with direction inferred from the action verb. A first-class delivery-log table with per-payload bytes/status is not yet wired.",
    acceptance: [
      "GET /integrations/sync-logs returns the latest N relevant events",
      "Each entry has direction in|out, source, event, status",
      "Limit clamped to 200",
      "Future: dedicated webhook_deliveries / integration_runs tables",
    ],
    source: ["worker/routes/integrations.ts:105"],
    priority: "P2",
    status: "partial",
  },
  {
    id: "FR-INTEGRATIONS-EXPLORER",
    category: "functional",
    area: "Integrations",
    title: "Embedded API explorer",
    description:
      "The Integrations page hosts an in-browser API explorer (endpoint picker, JSON request editor, response panel) so machine clients can validate against live endpoints without leaving the app.",
    acceptance: [
      "/integrations/explorer renders the explorer tab",
      "Endpoint selection drives the request editor placeholder",
      "Response panel pretty-prints JSON results",
    ],
    source: ["src/pages/ApiKeys.tsx:197", "src/pages/ApiKeys.tsx:305"],
    priority: "P2",
    status: "implemented",
  },

  // -------------------------------------------------------------------------
  // Search
  // -------------------------------------------------------------------------
  {
    id: "FR-SEARCH-FTS",
    category: "functional",
    area: "Search",
    title: "Cross-domain full-text search with LIKE fallback",
    description:
      "Search uses an SQLite FTS5 virtual table (`records_fts`) kept in sync via triggers. When FTS is empty or throws, the route falls back to a LIKE scan; domains matching the query are also surfaced as suggestions.",
    acceptance: [
      "GET /api/search?q=foo returns mixed record + domain hits",
      "FTS results are scored via bm25() and ranked highest",
      "LIKE fallback fills the page when FTS yields nothing",
      "Triggers maintain records_fts on insert/update/delete",
    ],
    source: ["worker/routes/search.ts:7", "worker/db/schema.sql:57"],
    priority: "P0",
    status: "implemented",
  },
  {
    id: "FR-SEARCH-PALETTE",
    category: "functional",
    area: "Search",
    title: "⌘K command palette",
    description:
      "TopBar exposes a pill search button that opens a global command palette; the search route powers the autocomplete.",
    acceptance: [
      "⌘K opens the palette from any route",
      "Results stream from /api/search and link to the record",
      "Esc closes the palette and returns focus",
    ],
    source: [
      "src/components/navigation/TopBar.tsx:1",
      "src/components/navigation/TopBar.tsx:38",
    ],
    priority: "P1",
    status: "implemented",
  },

  // -------------------------------------------------------------------------
  // Audit
  // -------------------------------------------------------------------------
  {
    id: "FR-AUDIT-LOG",
    category: "functional",
    area: "Audit",
    title: "Tamper-evident audit log with filters",
    description:
      "Every mutating route writes an audit_events row capturing actor, action, target, optional diff, IP and user-agent. The Audit page filters by actor, action prefix and date range.",
    acceptance: [
      "GET /api/audit supports actor, action (prefix), from, to, page, pageSize",
      "Records expose IP and User-Agent when present",
      "Diff is parsed back into JSON before return",
      "Audit page renders a JSON inspector drawer per row",
    ],
    source: ["worker/routes/audit.ts:7", "src/pages/AuditLog.tsx:1"],
    priority: "P0",
    status: "implemented",
  },

  // -------------------------------------------------------------------------
  // Dashboard
  // -------------------------------------------------------------------------
  {
    id: "FR-DASHBOARD-OVERVIEW",
    category: "functional",
    area: "Dashboard",
    title: "Live KPIs, datasets, activity and pending queue",
    description:
      "The home dashboard pulls a single /stats/overview payload that aggregates totals, per-domain quality, top datasets, recent activity and the pending-approval queue (priority-sorted).",
    acceptance: [
      "GET /api/stats/overview returns totals, perDomainQuality[], datasets[], activity[], pending[]",
      "pendingDelta is computed as today-vs-yesterday",
      "Pending list ordered by priority (critical>high>med>low) then maker_at",
      "Dashboard renders 4 KPI cards + Copilot card + datasets + activity timeline",
    ],
    source: ["worker/routes/stats.ts:7", "src/pages/Dashboard.tsx:1"],
    priority: "P0",
    status: "implemented",
  },

  // -------------------------------------------------------------------------
  // Settings
  // -------------------------------------------------------------------------
  {
    id: "FR-SETTINGS-PREFERENCES",
    category: "functional",
    area: "Settings",
    title: "Persist user preferences in KV",
    description:
      "GET /settings/me returns user identity + capabilities + preferences; PATCH /settings/me/preferences merges and stores the JSON blob under `prefs:<userId>` in CACHE KV.",
    acceptance: [
      "Preferences default to { theme:'system', density:'cozy', fontScale:'md' } on first load",
      "PATCH merges partial updates rather than replacing",
      "Capabilities are derived from the user's roles by the auth middleware",
    ],
    source: ["worker/routes/settings.ts:7", "worker/routes/settings.ts:18"],
    priority: "P1",
    status: "implemented",
  },
  {
    id: "FR-SETTINGS-UI-CONTROLS",
    category: "functional",
    area: "Settings",
    title: "Live appearance, a11y, notifications and retention controls",
    description:
      "Settings exposes tabbed controls for Appearance (theme/density/font), Accessibility (contrast/motion/focus/dyslexia), Notifications (per-event app/email) and Retention (versions/audit/soft-delete). Notifications and retention currently persist to localStorage only — no server endpoint or enforcement.",
    acceptance: [
      "Appearance changes apply live via useSettingsStore.applyToDocument()",
      "A11y toggles mutate <html> data attributes and inject a CSS shim",
      "Notification prefs persist server-side and gate email delivery",
      "Retention selectors drive a scheduled purge worker",
    ],
    source: [
      "src/pages/Settings.tsx:55",
      "src/pages/Settings.tsx:62",
      "src/pages/Settings.tsx:65",
    ],
    priority: "P1",
    status: "partial",
  },
  {
    id: "FR-SETTINGS-USERS-ROLES",
    category: "functional",
    area: "Settings",
    title: "Users and roles management",
    description:
      "Settings shows a Members table and a Permissions matrix (Admin/Steward/Contributor/Viewer/Auditor). The UI is static — there is no server endpoint to mutate users or roles, and the spec'd PUT /settings/users/:id/roles is not implemented.",
    acceptance: [
      "Members table lists all users with name/email/role/last-seen from /settings/users",
      "PUT /settings/users/:id/roles updates assignments and audits the change",
      "Permissions matrix is driven by the live role registry",
      "Invite / promote / demote / remove flows write audit_events",
    ],
    source: ["src/pages/Settings.tsx:170", "SPEC.md:185"],
    priority: "P1",
    status: "planned",
  },

  // -------------------------------------------------------------------------
  // Non-functional
  // -------------------------------------------------------------------------
  {
    id: "NFR-A11Y-WCAG",
    category: "non-functional",
    area: "Accessibility",
    title: "WCAG 2.1 AA compliance across every route",
    description:
      "All 11 primary routes are scanned with axe-core (wcag2a/wcag2aa/wcag21aa) under Playwright; the build fails on any serious or critical violation.",
    acceptance: [
      "tests/e2e/a11y.spec.ts asserts zero serious/critical violations on all main routes",
      "Interactive elements are reachable and operable by keyboard",
      "Icon-only buttons carry aria-label",
      "Live regions announce async actions when SR announcements are enabled",
    ],
    source: ["tests/e2e/a11y.spec.ts:1", "src/pages/Settings.tsx:146"],
    priority: "P0",
    status: "implemented",
  },
  {
    id: "NFR-A11Y-PREFERENCES",
    category: "non-functional",
    area: "Accessibility",
    title: "High-contrast, reduced motion, dyslexia font, focus strength",
    description:
      "Users can opt into high-contrast palette, reduced motion (prefers-reduced-motion override), an OpenDyslexic font, and three focus-outline strengths. Preferences apply live to <html> via data attributes and a shimmed stylesheet.",
    acceptance: [
      "High-contrast toggle remaps outline + on-surface tokens to maximum contrast",
      "Reduced motion zeros all transitions/animations via media query override",
      "Dyslexia font swaps the global font-family on <html> when enabled",
      "Focus strength sets outline width (1/2/4px) on :focus-visible",
    ],
    source: ["src/pages/Settings.tsx:33", "src/pages/Settings.tsx:119"],
    priority: "P1",
    status: "implemented",
  },
  {
    id: "NFR-PERF-TTI",
    category: "non-functional",
    area: "Performance",
    title: "Page TTI < 2.5s on cable, < 5s on 4G",
    description:
      "Code-split heavy pages (Lineage, ImportWizard) via React.lazy. DataGrid scrolls 60fps at 50k rows via @tanstack/react-virtual. Continuous TTI monitoring in CI is not yet wired.",
    acceptance: [
      "TTI under 2.5s on a 25 Mbps connection from cold cache",
      "DataGrid sustains 60fps while scrolling 50k virtual rows",
      "Each route is reachable in <1s warm-cache",
      "Future: Lighthouse CI budget gate on every PR",
    ],
    source: ["src/pages/Browse.tsx:1", "SPEC.md:255"],
    priority: "P1",
    status: "partial",
  },
  {
    id: "NFR-PERF-BUNDLE",
    category: "non-functional",
    area: "Performance",
    title: "Main bundle under 250 KB gzipped",
    description:
      "Main chunk stays under 250 KB gz with route-level code splitting; the airgap single-file build (`npm run build:single`) inlines everything for offline use. No automated size budget gate is configured yet.",
    acceptance: [
      "vite build reports main chunk < 250 KB gzipped",
      "Heavy pages lazy-load on first visit",
      "`npm run build:single` produces a self-contained dist/index.html",
      "Future: rollup-plugin-visualizer + size-limit gate in CI",
    ],
    source: ["SPEC.md:256"],
    priority: "P1",
    status: "partial",
  },
  {
    id: "NFR-SECURITY-SECRETS",
    category: "non-functional",
    area: "Security",
    title: "Secrets are hashed; raw values never returned twice",
    description:
      "API keys and webhook secrets are stored as SHA-256 hashes alongside an 8-char prefix; the raw value is returned only at creation time.",
    acceptance: [
      "api_keys table has no plaintext column",
      "Webhook secrets stored as hash + prefix only",
      "List endpoints omit the hash and never the raw secret",
    ],
    source: ["worker/routes/integrations.ts:8", "worker/routes/integrations.ts:33"],
    priority: "P0",
    status: "implemented",
  },
  {
    id: "NFR-SECURITY-VALIDATION",
    category: "non-functional",
    area: "Security",
    title: "Validate inputs and parameterise queries",
    description:
      "All D1 queries use bound parameters; sort columns are whitelisted; numeric query params are parsed with explicit clamping. A formal Zod schema layer at every POST/PATCH boundary is not yet in place.",
    acceptance: [
      "No string concatenation of user input into SQL",
      "Sort columns rejected unless on the allow-list",
      "Page sizes are clamped to documented maxima",
      "Future: Zod schema validation at every POST/PATCH boundary",
    ],
    source: ["worker/routes/records.ts:84", "worker/routes/audit.ts:7"],
    priority: "P0",
    status: "partial",
  },
  {
    id: "NFR-SECURITY-AUTH",
    category: "non-functional",
    area: "Security",
    title: "Scoped session + bearer-key permissions",
    description:
      "Auth middleware currently stubs a demo user. The launch plan replaces it with Cloudflare Access JWT verification (or upstream OIDC); bearer keys carry scopes that must be enforced per route.",
    acceptance: [
      "Unauthenticated requests are rejected on every /api/* route",
      "Bearer keys are matched by hashed lookup, not raw",
      "Capabilities (records.write.direct, approvals.approve, …) gate each handler",
      "Token rotation and revocation are honoured on the next request",
    ],
    source: ["SPEC.md:265", "worker/routes/integrations.ts:33"],
    priority: "P0",
    status: "planned",
  },
  {
    id: "NFR-RELIABILITY-EDGE",
    category: "non-functional",
    area: "Reliability",
    title: "Globally edge-deployed with no single point of failure",
    description:
      "The Worker runs on Cloudflare's global edge backed by D1 (regional read replicas), KV (eventually consistent global cache) and R2 (multi-region object store). Automatic retry/backoff on transient downstream errors and a graceful read-only degradation mode are still to come.",
    acceptance: [
      "wrangler.toml binds DB (D1), CACHE (KV), FILES (R2)",
      "Custom domain `rdm.davicity.com.au` plus workers.dev fallback",
      "Failover to a degraded read-only mode on D1 outage",
      "Automatic retries with backoff on transient downstream errors",
    ],
    source: ["wrangler.toml:1", "wrangler.toml:19"],
    priority: "P1",
    status: "partial",
  },
  {
    id: "NFR-OBSERVABILITY",
    category: "non-functional",
    area: "Observability",
    title: "Audit log + Workers tail + health endpoint",
    description:
      "Every audit-relevant mutation writes an audit_events row; Cloudflare `[observability]` collects request logs; `/api/health` returns a synthetic readiness probe.",
    acceptance: [
      "wrangler.toml enables [observability]",
      "GET /api/health returns { ok, name, env, at }",
      "Audit log covers create/update/delete/restore/approve/reject/withdraw/reparent/verify/upload/confirm",
    ],
    source: [
      "wrangler.toml:40",
      "worker/routes/records.ts:28",
      "SPEC.md:188",
    ],
    priority: "P1",
    status: "implemented",
  },
  {
    id: "NFR-DEPLOYMENT",
    category: "non-functional",
    area: "Deployment",
    title: "Cloudflare Workers + D1 + KV + R2 single-bundle deploy",
    description:
      "Build pipeline runs `npm run build` to produce ./dist (SPA assets, served by Workers Assets binding) plus the Worker itself. Deploys are zero-downtime via wrangler.",
    acceptance: [
      "[build] command = `npm run build` defined in wrangler.toml",
      "Assets binding serves the SPA with not_found_handling='single-page-application'",
      "compatibility_date pinned (2025-05-01); nodejs_compat flag enabled",
      "Every v* release rebuilds the airgap dist/index.html via build:single",
    ],
    source: ["wrangler.toml:1", "wrangler.toml:44"],
    priority: "P0",
    status: "implemented",
  },
  {
    id: "NFR-DATA-MODEL",
    category: "non-functional",
    area: "Data",
    title: "D1 SQLite with FTS5, JSON payloads and soft-delete",
    description:
      "Records carry a free-form JSON `data` column, are keyed by (domain_id, id), and use a soft-delete status. FTS5 powers cross-domain search, kept in sync by triggers. Retention policies are exposed in Settings but not yet enforced server-side by a purge worker.",
    acceptance: [
      "All FKs declared ON DELETE CASCADE where parent removal should clean up children",
      "records_fts is rebuilt on every insert/update/delete via triggers",
      "Soft-delete sets status='deprecated' and preserves the row",
      "Retention policy in Settings becomes a scheduled purge worker",
    ],
    source: [
      "worker/db/schema.sql:7",
      "worker/db/schema.sql:57",
      "worker/db/schema.sql:62",
      "worker/routes/records.ts:164",
    ],
    priority: "P0",
    status: "partial",
  },
];

export const groups: RequirementGroup[] = [
  {
    area: "Records",
    description: "Core CRUD, versioning and export for every domain table.",
    requirements: [
      "FR-RECORDS-CRUD",
      "FR-RECORDS-LIST",
      "FR-RECORDS-VERSIONING",
      "FR-RECORDS-EXPORT",
      "FR-RECORDS-DETAIL",
    ],
  },
  {
    area: "Domains",
    description: "Domain registry, field schemas and ownership.",
    requirements: ["FR-DOMAINS-LIST"],
  },
  {
    area: "Versions",
    description: "Immutable history with diff and restore.",
    requirements: ["FR-VERSIONS-DIFF", "FR-VERSIONS-RESTORE"],
  },
  {
    area: "Approvals",
    description: "Maker-checker workflow with comments, bulk actions and SLA.",
    requirements: [
      "FR-APPROVALS-FLOW",
      "FR-APPROVALS-BULK",
      "FR-APPROVALS-DUAL-CONTROL",
      "FR-APPROVALS-COMMENTS",
      "FR-APPROVALS-SLA",
    ],
  },
  {
    area: "Hierarchies",
    description: "Taxonomies with drag-reparent and cascade delete.",
    requirements: ["FR-HIERARCHIES-TREE", "FR-HIERARCHIES-MUTATIONS"],
  },
  {
    area: "Lineage",
    description: "Upstream/downstream graph with impact preview.",
    requirements: ["FR-LINEAGE-GRAPH", "FR-LINEAGE-IMPACT", "FR-LINEAGE-CONTROLS"],
  },
  {
    area: "Mappings",
    description: "Crosswalks between domains with confidence and verification.",
    requirements: ["FR-MAPPINGS-SETS", "FR-MAPPINGS-VERIFY"],
  },
  {
    area: "Quality",
    description: "Rule authoring, runs, violations and scorecards.",
    requirements: ["FR-QUALITY-RULES", "FR-QUALITY-RUNS", "FR-QUALITY-SCORECARD"],
  },
  {
    area: "Imports",
    description: "Bulk ingestion via R2 with mapping wizard.",
    requirements: ["FR-IMPORTS-UPLOAD", "FR-IMPORTS-COMMIT", "FR-IMPORTS-HISTORY"],
  },
  {
    area: "Integrations",
    description: "API keys, webhooks, sync logs and the API explorer.",
    requirements: [
      "FR-INTEGRATIONS-API-KEYS",
      "FR-INTEGRATIONS-WEBHOOKS",
      "FR-INTEGRATIONS-SYNC-LOGS",
      "FR-INTEGRATIONS-EXPLORER",
    ],
  },
  {
    area: "Search",
    description: "FTS5-powered cross-domain search + ⌘K palette.",
    requirements: ["FR-SEARCH-FTS", "FR-SEARCH-PALETTE"],
  },
  {
    area: "Audit",
    description: "Filterable, exportable, tamper-evident event log.",
    requirements: ["FR-AUDIT-LOG"],
  },
  {
    area: "Dashboard",
    description: "Single-payload home overview.",
    requirements: ["FR-DASHBOARD-OVERVIEW"],
  },
  {
    area: "Settings",
    description: "User preferences, a11y, notifications, retention, users.",
    requirements: [
      "FR-SETTINGS-PREFERENCES",
      "FR-SETTINGS-UI-CONTROLS",
      "FR-SETTINGS-USERS-ROLES",
    ],
  },
  {
    area: "Accessibility",
    description: "WCAG 2.1 AA enforced in CI plus user-tunable a11y prefs.",
    requirements: ["NFR-A11Y-WCAG", "NFR-A11Y-PREFERENCES"],
  },
  {
    area: "Performance",
    description: "TTI and bundle-size budgets, 60fps virtualised tables.",
    requirements: ["NFR-PERF-TTI", "NFR-PERF-BUNDLE"],
  },
  {
    area: "Security",
    description: "Hashed secrets, validated inputs, scoped auth.",
    requirements: [
      "NFR-SECURITY-SECRETS",
      "NFR-SECURITY-VALIDATION",
      "NFR-SECURITY-AUTH",
    ],
  },
  {
    area: "Reliability",
    description: "Globally edge-deployed with no single point of failure.",
    requirements: ["NFR-RELIABILITY-EDGE"],
  },
  {
    area: "Observability",
    description: "Audit log + Workers tail + health endpoint.",
    requirements: ["NFR-OBSERVABILITY"],
  },
  {
    area: "Deployment",
    description: "Cloudflare Workers + D1 + KV + R2.",
    requirements: ["NFR-DEPLOYMENT"],
  },
  {
    area: "Data",
    description: "SQLite + FTS5 + JSON payloads with retention.",
    requirements: ["NFR-DATA-MODEL"],
  },
];

const byStatus = requirements.reduce(
  (acc, r) => {
    acc[r.status] += 1;
    return acc;
  },
  { implemented: 0, partial: 0, planned: 0 } as Record<Requirement["status"], number>,
);

const byPriority = requirements.reduce(
  (acc, r) => {
    acc[r.priority] += 1;
    return acc;
  },
  { P0: 0, P1: 0, P2: 0 } as Record<Requirement["priority"], number>,
);

export const meta = {
  generatedAt: "2026-05-15T00:00:00.000Z",
  totalRequirements: requirements.length,
  totalCount: requirements.length,
  implementedCount: byStatus.implemented,
  partialCount: byStatus.partial,
  plannedCount: byStatus.planned,
  byStatus,
  byPriority,
} as const;

// Legacy aliases for earlier-built About page components.
export type RequirementStatus = Requirement["status"];
export type RequirementPriority = Requirement["priority"];
